// src/components/Billing/PaymentModal.js - Plan-based model (Free / Vendor / Vendor+)

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { X, CreditCard, Check, AlertCircle, DollarSign, Star, ArrowUpCircle } from 'lucide-react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import getStripe from '../../utils/stripe';
import {
  formatCurrency,
  PLANS,
  PLAN_ORDER,
  getPlanFromBilling,
  isMonthlyDue,
  getMonthlyAmount,
  getPlanPerks,
} from '../../utils/billingTiers';

// ─── Styled Components ────────────────────────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  pointer-events: auto;
  animation: paymentFadeIn 0.3s ease-out both;
  @keyframes paymentFadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const ModalContent = styled.div`
  background: ${props => props.theme?.colors?.surface || '#1a1a1a'};
  border: 2px solid ${props => props.theme?.colors?.accent};
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  position: fixed !important;
  top: 80px !important;
  transform: translateX(-50%) !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
  animation: paymentFadeInUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
  transform: none !important;
  right: auto !important;
  @keyframes paymentFadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${props => props.theme?.colors?.accent}; border-radius: 3px; }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid ${props => `${props.theme?.colors?.accent}30`};
  h2 { margin: 0; font-size: 1.4rem; color: ${props => props.theme?.colors?.accent}; display: flex; align-items: center; gap: 0.75rem; }
  @media (max-width: 768px) { padding: 1.25rem; h2 { font-size: 1.2rem; } }
`;

const CloseButton = styled.button`
  background: transparent; border: none; color: ${props => props.theme?.colors?.text};
  cursor: pointer; padding: 0.5rem; border-radius: 50%; transition: all 0.3s;
  &:hover { background: ${props => `${props.theme?.colors?.accent}20`}; transform: rotate(90deg); }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  @media (max-width: 768px) { padding: 1.25rem; }
`;

const PlanCard = styled.div`
  border-radius: 12px;
  border: 2px solid ${props => props.$selected ? props.$color : 'rgba(255,255,255,0.12)'};
  background: ${props => props.$selected ? `${props.$color}15` : 'rgba(255,255,255,0.03)'};
  padding: 1.1rem 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$selected ? `0 0 14px ${props.$color}35` : 'none'};
  margin-bottom: 0.75rem;
  &:hover { border-color: ${props => props.$color}; }
`;

const PayButton = styled.button`
  width: 100%;
  background: ${props => props.theme?.colors?.accent};
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CardElementContainer = styled.div`
  background: rgba(255,255,255,0.05);
  border: 1px solid ${props => `${props.theme?.colors?.accent}30`};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  &:focus-within { border-color: ${props => props.theme?.colors?.accent}; box-shadow: 0 0 0 2px ${props => `${props.theme?.colors?.accent}20`}; }
`;

const StatusMessage = styled.div`
  padding: 0.75rem 1rem; border-radius: 8px; display: flex; align-items: center; gap: 0.5rem;
  font-size: 0.9rem; margin-bottom: 1rem;
  &.error   { background: rgba(239,68,68,0.1); color: #ef4444; border: 1px solid rgba(239,68,68,0.3); }
  &.success { background: rgba(76,175,80,0.1); color: #4CAF50; border: 1px solid rgba(76,175,80,0.3); }
`;

const LoadingSpinner = styled.div`
  width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top-color: white;
  border-radius: 50%; animation: spin 0.8s linear infinite; margin-right: 8px;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// Colors + badges are still curated by hand; everything else (price, perks)
// is generated from billingTiers.js so this modal can't drift out of sync
// with the limits that are actually enforced.
const PLAN_COLORS = Object.fromEntries(Object.entries(PLANS).map(([key, p]) => [key, p.color]));
// Legacy aliases
PLAN_COLORS.vendor = PLAN_COLORS.starter;
PLAN_COLORS.vendor_plus = PLAN_COLORS.pro;

const PLAN_BADGES = { pro: 'Popular', elite: 'Best Value' };

const ALL_UPGRADE_PLANS = PLAN_ORDER
  .filter(key => key !== 'free')
  .map(key => ({
    key,
    color: PLANS[key].color,
    price: PLANS[key].price,
    badge: PLAN_BADGES[key] || null,
    perks: getPlanPerks(key),
  }));

// Legacy aliases (vendor/vendor_plus) map to the same rank as their modern equivalents
const PLAN_ORDER_INDEX = {
  ...Object.fromEntries(PLAN_ORDER.map((key, i) => [key, i])),
  vendor: 1,
  vendor_plus: 2,
};

// ─── Plan Selection Step ──────────────────────────────────────────────────────
const PlanSelector = ({ currentPlan, openFrom, onSelect, theme }) => {
  const isUpgradeOnly = openFrom === 'upgrade';

  // When upgrading, only show plans above current
  const plans = ALL_UPGRADE_PLANS.filter(p =>
    !isUpgradeOnly || (PLAN_ORDER_INDEX[p.key] || 0) > (PLAN_ORDER_INDEX[currentPlan] || 0)
  );

  const defaultPlan = plans.length > 0 ? plans[0].key : 'starter';
  const [selected, setSelected] = useState(defaultPlan);

  return (
    <div>
      <p style={{ color: theme?.colors?.text, opacity: 0.75, fontSize: '0.9rem', marginBottom: '1.25rem', marginTop: 0 }}>
        {isUpgradeOnly
          ? 'Upgrade your plan to unlock more visibility slots and features.'
          : 'Choose a plan to start listing items.'}
      </p>

      {plans.map(plan => (
        <PlanCard
          key={plan.key}
          $selected={selected === plan.key}
          $color={plan.color}
          onClick={() => setSelected(plan.key)}
          style={{ position: 'relative', overflow: 'hidden' }}
        >
          {plan.badge && (
            <div style={{ position: 'absolute', top: 0, right: 0, background: plan.color, color: '#000', fontSize: '0.6rem', fontWeight: 800, padding: '0.15rem 0.5rem', borderBottomLeftRadius: 6 }}>
              {plan.badge}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ width: 13, height: 13, borderRadius: '50%', border: `2px solid ${plan.color}`, background: selected === plan.key ? plan.color : 'transparent', flexShrink: 0 }} />
              <span style={{ fontWeight: 800, fontSize: '1.05rem', color: plan.color }}>{PLANS[plan.key]?.label}</span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 900, fontSize: '1.1rem', color: 'white' }}>${plan.price}</div>
              <div style={{ fontSize: '0.72rem', color: plan.color, fontWeight: 600 }}>one-time</div>
            </div>
          </div>
          <ul style={{ margin: '0.75rem 0 0 1.5rem', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            {plan.perks.map((p, i) => (
              <li key={i} style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)' }}>{p}</li>
            ))}
          </ul>
        </PlanCard>
      ))}

      <PayButton
        as="button"
        onClick={() => onSelect(selected)}
        theme={theme}
        style={{ background: PLAN_COLORS[selected] || '#3b82f6' }}
      >
        <Star size={16} />
        Continue with {PLANS[selected]?.label || 'Plan'}
      </PayButton>
    </div>
  );
};

// ─── Payment Form ─────────────────────────────────────────────────────────────
const PaymentForm = ({ billingData, targetPlan, onSuccess, onCancel, theme }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [walletAvailable, setWalletAvailable] = useState(null);

  const currentPlan = getPlanFromBilling(billingData);
  const monthlyDue  = isMonthlyDue(billingData);

  // ── Determine what to charge ────────────────────────────────────────────
  let chargeAmount = 0;
  let chargeLabel  = '';

  const PLAN_PRICES = {
    starter: PLANS.starter.price, vendor: PLANS.starter.price,
    pro: PLANS.pro.price, vendor_plus: PLANS.pro.price,
    elite: PLANS.elite.price,
    premium: PLANS.premium.price,
  };
  if (monthlyDue) {
    chargeAmount = getMonthlyAmount(billingData);
    chargeLabel  = `${PLANS[currentPlan]?.label} Renewal — ${formatCurrency(chargeAmount)}`;
  } else if (targetPlan && PLAN_PRICES[targetPlan]) {
    chargeAmount = PLAN_PRICES[targetPlan];
    chargeLabel  = `${PLANS[targetPlan]?.label || targetPlan} Setup Fee — ${formatCurrency(chargeAmount)}`;
  }

  const discountPercent = promoApplied?.discountPercent || 0;
  const discountAmount  = (chargeAmount * discountPercent) / 100;
  const finalAmount     = Math.max(0, chargeAmount - discountAmount);

  useEffect(() => {
    if (!stripe || finalAmount <= 0) return;
    const pr = stripe.paymentRequest({
      country: 'US', currency: 'usd',
      total: { label: 'KalKode Plan Fee', amount: Math.round(finalAmount * 100) },
      requestPayerName: false, requestPayerEmail: false,
    });
    pr.canMakePayment().then(result => {
      setWalletAvailable(!!result);
      if (result) setPaymentRequest(pr);
    });
    pr.on('paymentmethod', async ev => {
      try {
        const result = await submitPayment(ev.paymentMethod.id);
        if (result.error) { ev.complete('fail'); setError(result.error); }
        else { ev.complete('success'); setSuccess(true); onSuccess(result); }
      } catch (err) { ev.complete('fail'); setError(err.message); }
    });
  }, [stripe, finalAmount]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true); setPromoError(''); setPromoApplied(null);
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../../firebase/config');
      const snap = await getDoc(doc(db, 'promoCodes', 'codes'));
      if (!snap.exists()) { setPromoError('Invalid promo code.'); return; }
      const codes = snap.data().list || [];
      const match = codes.find(p =>
        p.code.trim().toLowerCase() === promoCode.trim().toLowerCase() && p.active !== false
      );
      if (!match) setPromoError('Invalid or expired promo code.');
      else setPromoApplied(match);
    } catch { setPromoError('Could not validate. Try again.'); }
    finally { setPromoLoading(false); }
  };

  const submitPayment = async (paymentMethodId) => {
    const apiUrl = process.env.NODE_ENV === 'production'
      ? 'https://www.kalkode.com/api/process-billing-payment'
      : '/api/process-billing-payment';

    const res = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        operation: monthlyDue ? 'monthly_payment' : 'activate_plan',
        paymentMethodId,
        userId: billingData.userId,
        ownerUserId: billingData.ownerUserId || billingData.userId,
        targetPlan: monthlyDue ? currentPlan : targetPlan,
        amount: finalAmount,
        originalAmount: chargeAmount,
        promoCode: promoApplied?.code || null,
        discountPercent,
      }),
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Payment failed');
    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || success) return;
    if (finalAmount > 0 && !stripe) return;
    setLoading(true); setError(null);

    try {
      if (finalAmount === 0) {
        // 100% promo
        const result = await submitPayment('promo_free');
        setSuccess(true);
        onSuccess(result);
        return;
      }
      if (!elements) return;
      const cardElement = elements.getElement(CardElement);
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card', card: cardElement,
        billing_details: { name: billingData.userName || 'Shop Owner' },
      });
      if (pmError) throw new Error(pmError.message);
      const result = await submitPayment(paymentMethod.id);
      setSuccess(true);
      onSuccess(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const planColor = PLAN_COLORS[monthlyDue ? currentPlan : (targetPlan || currentPlan)] || '#3b82f6';

  return (
    <form onSubmit={handleSubmit}>
      {/* Charge Summary */}
      <div style={{
        background: `${planColor}12`,
        border: `1px solid ${planColor}35`,
        borderRadius: '12px',
        padding: '1.25rem',
        marginBottom: '1.5rem',
      }}>
        <div style={{ fontSize: '2.2rem', fontWeight: 800, color: planColor, marginBottom: '0.25rem' }}>
          {formatCurrency(finalAmount)}
        </div>
        {promoApplied && chargeAmount !== finalAmount && (
          <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.4)', textDecoration: 'line-through', marginBottom: '0.2rem' }}>
            {formatCurrency(chargeAmount)}
          </div>
        )}
        <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{chargeLabel}</div>
        {!monthlyDue && (
          <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.5rem' }}>
            One-time fee — no recurring charges
          </div>
        )}
      </div>

      {/* Promo Code */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ padding: '1rem', background: 'rgba(255,255,255,0.03)', border: `1px solid rgba(255,255,255,0.1)`, borderRadius: '10px' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: '0.6rem' }}>🏷️ Promo Code</div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              placeholder="Enter promo code..."
              value={promoCode}
              onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoApplied(null); setPromoError(''); }}
              disabled={!!promoApplied || success}
              style={{
                flex: 1, background: 'rgba(255,255,255,0.07)', border: `1px solid ${promoApplied ? 'rgba(76,175,80,0.6)' : 'rgba(255,255,255,0.15)'}`,
                borderRadius: '8px', padding: '0.7rem 1rem', color: 'white', fontSize: '0.9rem', outline: 'none', fontFamily: 'monospace',
              }}
            />
            <button
              type="button" onClick={handleApplyPromo}
              disabled={!promoCode.trim() || !!promoApplied || promoLoading || success}
              style={{ background: promoApplied ? 'rgba(76,175,80,0.2)' : 'rgba(255,255,255,0.08)', border: `1px solid ${promoApplied ? 'rgba(76,175,80,0.5)' : 'rgba(255,255,255,0.2)'}`, color: promoApplied ? '#4CAF50' : 'rgba(255,255,255,0.8)', padding: '0.7rem 1.25rem', borderRadius: '8px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {promoLoading ? '...' : promoApplied ? '✓ Applied' : 'Apply'}
            </button>
          </div>
          {promoApplied && (
            <div style={{ marginTop: '0.6rem', fontSize: '0.82rem', color: '#4CAF50', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Check size={13} />
              {promoApplied.discountPercent === 100 ? 'No payment required!' : `${promoApplied.discountPercent}% off — saving ${formatCurrency(discountAmount)}`}
            </div>
          )}
          {promoError && <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#ef4444' }}>{promoError}</div>}
        </div>
      </div>

      {/* Apple Pay / Google Pay */}
      {finalAmount > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          {walletAvailable === null && <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, height: 48 }} />}
          {walletAvailable === true && paymentRequest && (
            <div style={{ borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
              <PaymentRequestButtonElement options={{ paymentRequest, style: { paymentRequestButton: { type: 'default', theme: 'dark', height: '48px' } } }} />
            </div>
          )}
          {walletAvailable !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '1rem 0' }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>or pay with card</span>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
            </div>
          )}
        </div>
      )}

      {/* Card Input */}
      {finalAmount > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem', color: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CreditCard size={18} />Payment Method
          </h3>
          <CardElementContainer theme={theme}>
            <CardElement options={{ style: { base: { fontSize: '16px', color: '#ffffff', '::placeholder': { color: 'rgba(255,255,255,0.5)' }, backgroundColor: 'transparent' } }, hidePostalCode: true, disableLink: true }} />
          </CardElementContainer>
        </div>
      )}

      {error   && <StatusMessage className="error"><AlertCircle size={16} />{error}</StatusMessage>}
      {success && <StatusMessage className="success"><Check size={16} />Payment successful! Listings updated.</StatusMessage>}

      <PayButton type="submit" disabled={loading || success || (finalAmount > 0 && !stripe)} theme={theme} style={{ background: planColor }}>
        {loading ? <><LoadingSpinner />Processing...</>
          : success ? <><Check size={20} />Complete</>
          : finalAmount === 0 ? <><Check size={20} />Apply Free Access</>
          : <><DollarSign size={20} />Pay {formatCurrency(finalAmount)}</>}
      </PayButton>

      <div style={{ textAlign: 'center', fontSize: '0.8rem', opacity: 0.5, marginTop: '1rem', color: 'white' }}>
        🔒 Payments secured by Stripe
      </div>
    </form>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
const PaymentModal = ({ billingData, onClose, onSuccess, theme }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [step, setStep] = useState('plan'); // 'plan' | 'pay'
  const [targetPlan, setTargetPlan] = useState(null);

  useEffect(() => { setStripePromise(getStripe()); }, []);

  // Lock body scroll
  useEffect(() => {
    const orig = document.body.style.overflow;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollBarWidth > 0) document.body.style.paddingRight = `${scrollBarWidth}px`;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = orig;
      document.body.style.paddingRight = '';
    };
  }, []);

  const currentPlan = getPlanFromBilling(billingData);
  const monthly     = isMonthlyDue(billingData);
  const openFrom    = billingData?.openFrom || (monthly ? 'payment' : 'start');

  useEffect(() => {
    if (monthly) {
      setTargetPlan(currentPlan);
      setStep('pay');
    } else if (openFrom === 'upgrade') {
      const plansAbove = ALL_UPGRADE_PLANS.filter(p => (PLAN_ORDER_INDEX[p.key] || 0) > (PLAN_ORDER_INDEX[currentPlan] || 0));
      if (plansAbove.length === 0) return;
      setTargetPlan(plansAbove[0].key);
      // Only one option above current (e.g. Pro → Elite): go straight to payment
      if (plansAbove.length === 1) {
        setStep('pay');
      } else {
        setStep('plan');
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlanSelected = (plan) => {
    setTargetPlan(plan);
    setStep('pay');
  };

  const titleLabel = monthly
    ? `${PLANS[currentPlan]?.label} Renewal`
    : 'Upgrade Menu';

  const TitleIcon = monthly ? DollarSign : openFrom === 'upgrade' ? ArrowUpCircle : Star;

  const modal = (
    <ModalOverlay onClick={onClose}>
      <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <h2><TitleIcon size={22} />{titleLabel}</h2>
          <CloseButton onClick={onClose} theme={theme}><X size={24} /></CloseButton>
        </ModalHeader>

        <ModalBody>
          {step === 'plan' && (
            <PlanSelector
              currentPlan={currentPlan}
              openFrom={openFrom}
              onSelect={handlePlanSelected}
              theme={theme}
            />
          )}

          {step === 'pay' && targetPlan && (
            <Elements stripe={stripePromise} options={{ wallets: { link: 'never' } }}>
              <PaymentForm
                billingData={billingData}
                targetPlan={targetPlan}
                onSuccess={onSuccess}
                onCancel={onClose}
                theme={theme}
              />
            </Elements>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );

  return createPortal(modal, document.body);
};

export default PaymentModal;
